import React, { createContext, useContext, useReducer, useMemo } from 'react';
import { clinicalDataService } from '../services/clinicalDataService';

// Initial state
const initialState = {
  company: {
    id: 'company-1',
    name: '',
    address: '',
    phone: '',
    email: '',
    officialRep: '',
    medicalRep: '',
    entityType: 'multi-specialty',
    totalBeds: 0,
    infectionControlPolicy: {},
    qualityManagementSystem: '',
    riskManagementFramework: '',
    haiTargetRates: {},
    complianceTargets: {},
    qualityIndicators: [],
    patientSafetyGoals: [],
    costPerBed: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  locations: [
    {
      id: 'location-1',
      organizationId: 'company-1',
      name: 'Main Campus',
      address: '123 Medical Center Dr',
      contactPerson: 'John Smith',
      phone: '(555) 123-4567',
      email: 'main@hospital.com',
      status: 'active',
      emergencyServices: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  ],
  buildings: [],
  floors: [],
  departments: [],
  rooms: [],
  beds: [],
  equipment: [],
  personnel: [],
  biological_entities: [],
  icLeadership: {
    infectionControlOfficer: '',
    infectionControlEmail: '',
    infectionControlPhone: '',
    medicalDirectorIC: '',
    nurseManagerIC: '',
    infectionControlPolicy: ''
  },
  riskManagement: {
    riskAssessmentFrequency: '',
    baseRiskLevel: '',
    auditSchedule: '',
    reportingThreshold: '',
    surveillanceFrequency: ''
  },
  policyFramework: {
    icPolicies: [],
    trainingPrograms: [],
    emergencyProtocols: []
  },
  haiTargetRates: {
    cdi: '',
    cauti: '',
    clabsi: '',
    ssi: '',
    vap: '',
    handHygieneTarget: '',
    cleaningComplianceTarget: '',
    educationComplianceTarget: ''
  },
  // CRUD Lists
  departmentTypes: ['ICU', 'Emergency', 'Surgery', 'Pediatrics', 'General Ward'],
  roomTypes: ['Patient Room', 'Operating Room', 'Examination Room', 'ICU Room', 'Storage'],
  bedStates: ['Available', 'Occupied', 'Cleaning', 'Maintenance', 'Reserved'],
  equipmentTypes: ['MRI Scanner', 'CT Scanner', 'X-Ray', 'Ventilator', 'Patient Monitor', 'Ultrasound']
};

// Action types
const ACTIONS = {
  ADD_ITEM: 'ADD_ITEM',
  UPDATE_ITEM: 'UPDATE_ITEM',
  DELETE_ITEM: 'DELETE_ITEM',
  SET_STRUCTURE: 'SET_STRUCTURE',
  SET_SELECTED_ITEM: 'SET_SELECTED_ITEM',
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
  SET_ENTITY_FILTERS: 'SET_ENTITY_FILTERS'
};

// Reducer
const clinicalReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.ADD_ITEM:
      return clinicalDataService.addItem(state, action.payload.type, action.payload.parentId, action.payload.data);
    
    case ACTIONS.UPDATE_ITEM:
      return clinicalDataService.updateItem(state, action.payload.type, action.payload.id, action.payload.updates);
    
    case ACTIONS.DELETE_ITEM:
      return clinicalDataService.deleteItem(state, action.payload.type, action.payload.id);
    
    case ACTIONS.SET_STRUCTURE:
      return { ...state, ...action.payload };
    
    case ACTIONS.SET_SELECTED_ITEM:
      return { ...state, selectedItem: action.payload };
    
    case ACTIONS.SET_SEARCH_QUERY:
      return { ...state, searchQuery: action.payload };
    
    case ACTIONS.SET_ENTITY_FILTERS:
      return { 
        ...state, 
        entityFilterCategory: action.payload.category,
        entityFilterPriority: action.payload.priority,
        entityFilterResistance: action.payload.resistance,
        entityFilterStatus: action.payload.status
      };
    
    default:
      return state;
  }
};

// Context
const ClinicalContext = createContext();

// Provider component
export const ClinicalProvider = ({ children }) => {
  const [state, dispatch] = useReducer(clinicalReducer, initialState);

  const actions = useMemo(() => ({
    addItem: (type, parentId, data) => {
      dispatch({
        type: ACTIONS.ADD_ITEM,
        payload: { type, parentId, data }
      });
    },
    
    updateItem: (type, id, updates) => {
      dispatch({
        type: ACTIONS.UPDATE_ITEM,
        payload: { type, id, updates }
      });
    },
    
    deleteItem: (type, id) => {
      dispatch({
        type: ACTIONS.DELETE_ITEM,
        payload: { type, id }
      });
    },
    
    setStructure: (structure) => {
      dispatch({
        type: ACTIONS.SET_STRUCTURE,
        payload: structure
      });
    },
    
    setSelectedItem: (item) => {
      dispatch({
        type: ACTIONS.SET_SELECTED_ITEM,
        payload: item
      });
    },
    
    setSearchQuery: (query) => {
      dispatch({
        type: ACTIONS.SET_SEARCH_QUERY,
        payload: query
      });
    },
    
    setEntityFilters: (filters) => {
      dispatch({
        type: ACTIONS.SET_ENTITY_FILTERS,
        payload: filters
      });
    }
  }), []);

  const value = useMemo(() => ({ state, actions }), [state, actions]);

  return (
    <ClinicalContext.Provider value={value}>
      {children}
    </ClinicalContext.Provider>
  );
};

// Custom hook to use the context
export const useClinical = () => {
  const context = useContext(ClinicalContext);
  if (!context) {
    throw new Error('useClinical must be used within a ClinicalProvider');
  }
  return context;
};

export default ClinicalContext;
export { ClinicalContext };
